import type { Sight } from '@/types';

/**
 * Sight catalog. Covers the major sights bowhunters use today —
 * Ultraview, Spot Hogg, Black Gold, HHA, Axcel, IQ, Sword, Trophy Ridge.
 * Sliders + multi-pin + fixed-pin all represented.
 *
 * Pin diameters and sight radius ranges from current manufacturer pages.
 * Cross-check before publishing customer-facing setup sheets.
 */
export const SIGHTS: readonly Sight[] = [
  // ────────────────────────────────────────────────────────
  // ULTRAVIEW — verified May 2026 vs ultraviewarchery.com/collections/sights
  // Their CURRENT lineup has ONE standalone sight: the UV Slider 2.
  // UV3 / UV3XL are SCOPE HOUSINGS (not sights) that attach to Axcel, Shibuya,
  // CBE, ArcSystem target sights — we treat them as scopes separately.
  // Earlier UV1 / UV3 Single Pin / UV Slider 2 Pro / UV Compass entries were
  // incorrect or do not exist as standalone sights.
  // ────────────────────────────────────────────────────────
  { brand: 'Ultraview', model: 'UV Slider 2',        style: 'slider',
    pinDiameters: [0.010, 0.015, 0.019],  sightRadius: { min: 18, max: 32 },
    notes: 'Only standalone sight in Ultraview\'s current lineup. DualDial elevation+windage, Home Stop adjustable post, QuickBreak detachable scope assembly. Metal and vinyl tapes available. $499.99. Available in direct-mount, picatinny, and bridge-lock variants.' },
  { brand: 'Ultraview', model: 'UV Slider 2 (Mathews Bridge-Lock)', style: 'slider',
    pinDiameters: [0.010, 0.015, 0.019],  sightRadius: { min: 18, max: 32 },
    notes: 'UV Slider 2 with Mathews Bridge-Lock mount system — eliminates the dovetail bar, mounts directly into the Mathews bridge. Same internals as standard UVS2; mounting platform differs.' },

  // ────────────────────────────────────────────────────────
  // SPOT HOGG — the rugged workhorse
  // ────────────────────────────────────────────────────────
  { brand: 'Spot Hogg', model: 'Fast Eddie XL',     style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 30 },
    notes: 'Heavy-duty slider. 5- and 3-pin variants.' },
  { brand: 'Spot Hogg', model: 'Fast Eddie XL Triple Stack MRT', style: 'multi-pin',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 30 },
    notes: 'Triple Stack MRT — multiple MRT rings for any light condition. Widely regarded best in class.' },
  { brand: 'Spot Hogg', model: 'Fast Eddie',        style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 28 } },
  { brand: 'Spot Hogg', model: 'Boonie Series',     style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 22 },
    notes: 'Multiple MRT rings for any light condition. Heavy-duty hunting build.' },
  { brand: 'Spot Hogg', model: 'MRT Hunter (5-pin)', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 } },
  { brand: 'Spot Hogg', model: 'Boss Hogg',         style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 32 },
    notes: 'Tournament-grade fully adjustable.' },
  { brand: 'Spot Hogg', model: 'Hogg Father',       style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 } },
  { brand: 'Spot Hogg', model: 'Hogg Father Pro',   style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 30 },
    notes: 'Single-pin slider variant of the Hogg Father. Adjustable sight bar with the same heavy-duty MRT pin head.' },
  { brand: 'Spot Hogg', model: 'Real Deal',         style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 18 },
    notes: 'Compact 5-pin or 7-pin hunter.' },

  // ────────────────────────────────────────────────────────
  // BLACK GOLD — premium hunting
  // ────────────────────────────────────────────────────────
  { brand: 'Black Gold', model: 'Ascent Mountain Lite', style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 22 } },
  { brand: 'Black Gold', model: 'Ascent Verdict',       style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 28 } },
  { brand: 'Black Gold', model: 'Ascent Whitetail',     style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 } },
  { brand: 'Black Gold', model: 'Pro Dual',             style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 24 } },
  { brand: 'Black Gold', model: 'Pro Hunter',           style: 'multi-pin',
    pinDiameters: [0.019], sightRadius: { min: 14, max: 18 } },
  { brand: 'Black Gold', model: 'Whitetail Special',    style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 } },
  { brand: 'Black Gold', model: 'Rush',                 style: 'multi-pin',
    pinDiameters: [0.019], sightRadius: { min: 14, max: 18 } },
  { brand: 'Black Gold', model: 'Pure 3',               style: 'multi-pin',
    pinDiameters: [0.019], sightRadius: { min: 14, max: 18 } },
  { brand: 'Black Gold', model: 'Mojo 1810',            style: 'multi-pin',
    pinDiameters: [0.019], sightRadius: { min: 14, max: 18 },
    notes: 'Wide adjustment range for 3D + hunting.' },
  { brand: 'Black Gold', model: 'Dual Trac',            style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 26 },
    notes: 'Straight-rail single-pin slider. Linear tape (scale 1.0, verified vs PCA on Sicario/X10).' },
  { brand: 'Black Gold', model: 'Dual Trac Mountain Lite', style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 24 },
    notes: 'Lightweight Dual Trac. Straight-rail linear tape.' },

  // ────────────────────────────────────────────────────────
  // HHA — single-pin sliders, hunting heavy
  // ────────────────────────────────────────────────────────
  { brand: 'HHA', model: 'Tetra Max',     style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 30 } },
  { brand: 'HHA', model: 'Optimizer Lite Ultra', style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 24 } },
  { brand: 'HHA', model: 'Optimizer King Pin',   style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 30 } },
  { brand: 'HHA', model: 'Tetra',         style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 24 } },
  // RYZ geared-elevation family (verified vs hhasports.com, June 2026). The RYZ
  // mechanism prints a STEEPER per-yard tape than a linear slider — see
  // lib/sightCalibration (HHA RYZ/NYTRX ≈ 2.2× the UV linear scale).
  { brand: 'HHA', model: 'NYTRX Pro',     style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 30 },
    notes: 'Flagship NYTRX single-pin slider with the RYZ geared elevation system. Uses HHA RYZ sight tapes — a much steeper per-yard scale than a linear dovetail slider.' },
  { brand: 'HHA', model: 'NYTRX',         style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 30 },
    notes: 'NYTRX single-pin slider, RYZ geared elevation. Shares the RYZ tape scale.' },
  { brand: 'HHA', model: 'Tetra RYZ X3',  style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 28 },
    notes: 'Tetra with the RYZ geared elevation + X3 (3×) scope. Uses HHA RYZ tapes.' },
  { brand: 'HHA', model: 'Tetra Max RYZ', style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 30 },
    notes: 'Long-bar Tetra Max with the RYZ geared elevation system. Shares the RYZ tape scale.' },

  // ────────────────────────────────────────────────────────
  // DIALED ARCHERY — USA-made single-pin sliders (verified vs dialedarchery.com
  // + Lancaster Archery, June 2026). PRÜF series succeeds the ARXOS line:
  // swappable dovetail/picatinny mount, 1XR / 3V mag scopes, A.E.S. tech, ~9.8 oz.
  // Geared elevation prints a steeper-than-linear tape (Dialed ≈ 1.24× UV).
  // ────────────────────────────────────────────────────────
  { brand: 'Dialed Archery', model: 'Pruf',       style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 32 },
    notes: 'PRÜF single-pin slider. Swappable dovetail/picatinny mount, 1XR or 3V mag scope, A.E.S. tech, ~9.8 oz. Steeper per-yard tape scale than a linear slider.' },
  { brand: 'Dialed Archery', model: 'Pruf Mover', style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 32 },
    notes: 'PRÜF Mover variant of the PRÜF slider. Same elevation system / tape scale.' },

  // ────────────────────────────────────────────────────────
  // AXCEL — tournament gold standard
  // ────────────────────────────────────────────────────────
  // DRIVR — Axcel's newest (2025) flagship hunting slider, 8.6 oz lightest on market
  { brand: 'Axcel', model: 'DRIVR (Single Pin)',        style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 30 },
    notes: 'NEW 2025. 8.6 oz lightest slider on market. Dual Glide System. Metal tapes to 140+ yd. Bridgelock/Dovetail/Picatinny mounting.' },
  { brand: 'Axcel', model: 'DRIVR (3-Pin)',             style: 'multi-pin',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 30 },
    notes: 'NEW 2025. 3-pin variant of the DRIVR slider platform.' },
  { brand: 'Axcel', model: 'DRIVR (5-Pin)',             style: 'multi-pin',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 30 },
    notes: 'NEW 2025. 5-pin variant of the DRIVR slider platform.' },
  // Legacy + current models
  { brand: 'Axcel', model: 'Landslyde Slider',   style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 32 } },
  { brand: 'Axcel', model: 'Landslyde Plus',     style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 32 } },
  { brand: 'Axcel', model: 'AccuTouch Slider',   style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 30 } },
  { brand: 'Axcel', model: 'AccuStat Pro Hunter', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 16, max: 22 } },
  { brand: 'Axcel', model: 'Achieve XP Slider',   style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 20, max: 36 },
    notes: 'Olympic / target compound.' },
  { brand: 'Axcel', model: 'Sight One Slider',    style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 26 },
    notes: 'Value-tier slider option.' },

  // ────────────────────────────────────────────────────────
  // IQ — Retina Lock
  // ────────────────────────────────────────────────────────
  { brand: 'IQ', model: 'Pro One Pin',  style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 22 } },
  { brand: 'IQ', model: 'Define Pro',   style: 'multi-pin',
    pinDiameters: [0.019], sightRadius: { min: 14, max: 18 } },
  { brand: 'IQ', model: 'Ultralite 5-Pin', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 18 } },

  // ────────────────────────────────────────────────────────
  // SWORD / TROPHY RIDGE / OTHER
  // ────────────────────────────────────────────────────────
  { brand: 'Sword', model: 'Acu-Site Pro',         style: 'multi-pin',
    pinDiameters: [0.019], sightRadius: { min: 14, max: 18 } },
  { brand: 'Trophy Ridge', model: 'React Pro',     style: 'multi-pin',
    pinDiameters: [0.019], sightRadius: { min: 14, max: 20 },
    notes: 'Self-adjusting pin spacing.' },
  { brand: 'Trophy Ridge', model: 'Volt 5-Pin',    style: 'multi-pin',
    pinDiameters: [0.019], sightRadius: { min: 14, max: 18 } },
  { brand: 'TruGlo', model: 'Carbon Hybrid Micro', style: 'slider',
    pinDiameters: [0.019], sightRadius: { min: 14, max: 22 } },
  { brand: 'CBE', model: 'CX5',                    style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 } },
  { brand: 'CBE', model: 'Trek Pro',               style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 26 } },

  // ════════════════════════════════════════════════════════
  // TARGET SIGHTS — competition compounds (Vegas, NFAA, WA720, indoor, 3D)
  // Long sight radius (18-32"), low-magnification scope housings, fine-fiber
  // pins / dot inserts. Verified May 2026 from manufacturer + Lancaster
  // Archery Supply category pages.
  // ════════════════════════════════════════════════════════

  // ──── Bowfinger — 1 Sight V2 + V1 (no "Defiant" — that name is a misnomer)
  { brand: 'Bowfinger', model: '1 Sight V2', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 20, max: 32 },
    notes: 'Flagship target slider. Dovetail windage, double-gibb slides, .0015"/click W. 5/16 sq scope barrel. Ships with 15 metal tapes (30 scales).' },
  { brand: 'Bowfinger', model: '1 Sight (V1)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 20, max: 32 },
    notes: 'Original 1 Sight platform. Rail-based windage.' },

  // ──── Shrewd — Nomad Pro + Optum scope system
  { brand: 'Shrewd', model: 'Nomad Pro (with Optum Scope)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029, 0.039], sightRadius: { min: 18, max: 32 },
    notes: 'Top-tier target slider. Optum scopes (29/35/40mm). ORS removable rings, 8 pin positions, dual-fiber option. Vegas/Lancaster mainstay.' },
  { brand: 'Shrewd', model: 'Optum Scope 29mm', style: 'scope',
    pinDiameters: [0.010, 0.019, 0.029, 0.039], sightRadius: { min: 18, max: 32 },
    notes: 'Scope only — pairs with Shrewd Nomad, Axcel, Spot-Hogg, Sure-Loc, B3 chassis.' },
  { brand: 'Shrewd', model: 'Optum Scope 35mm', style: 'scope',
    pinDiameters: [0.010, 0.019, 0.029, 0.039], sightRadius: { min: 18, max: 32 },
    notes: 'Mid-size Optum scope (35mm housing).' },
  { brand: 'Shrewd', model: 'Optum Scope 40mm', style: 'scope',
    pinDiameters: [0.010, 0.019, 0.029, 0.039], sightRadius: { min: 18, max: 32 },
    notes: 'Largest Optum scope (40mm) — long-distance / known-distance target.' },

  // ──── Axcel — Achieve + AccuTouch target line (distinct from DRIVR/Landslyde hunting)
  { brand: 'Axcel', model: 'Achieve XP Pro', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 20, max: 32 },
    notes: 'Premier tournament slider. Single-screw balanced W&E tension, lock-down zero movement, micro yardage pointer w/ metal tapes. 6"/9" extensions.' },
  { brand: 'Axcel', model: 'Achieve XP (carbon bar)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 20, max: 32 },
    notes: 'Step below XP Pro. Carbon extension option.' },
  { brand: 'Axcel', model: 'Achieve CXL', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 20, max: 30 },
    notes: 'Established tournament slider. Dovetail windage, quick-slide half-engage tension lock. 6"/9".' },
  { brand: 'Axcel', model: 'Achieve CX', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 20, max: 30 },
    notes: 'Lighter sibling of CXL.' },
  { brand: 'Axcel', model: 'AccuTouch HD', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 28 },
    notes: 'Single-pin slider with Accu-Clicks (mimic multi-pin). Crosses target/3D/hunting.' },
  { brand: 'Axcel', model: 'AccuTouch Plus HD (w/ AccuView Scope)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 28 },
    notes: 'AccuTouch HD bundled with AccuView X-31 or X-41 scope, Fire Pin + crosshair + rheostat.' },

  // ──── Shibuya — Ultima CPX series (Japanese, dominant at international comps)
  { brand: 'Shibuya', model: 'Ultima CPX-III 400', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 32 },
    notes: 'Current flagship (CPX-III replaced CPX-II ~2024). Carbon extension, Tria-Lock scope rod, X-Lock W, ZPE elevation, .002"/click micro. 4" elevation travel.' },
  { brand: 'Shibuya', model: 'Ultima CPX-III 520', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 32 },
    notes: 'CPX-III with extended 5.2" elevation travel for longer-distance / extreme DA.' },
  { brand: 'Shibuya', model: 'Ultima CPX-II 365', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 32 },
    notes: 'Previous generation, still widely sold. 3.65" elevation travel. 10-32 scope thread.' },
  { brand: 'Shibuya', model: 'Ultima CPX-II 485', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 32 },
    notes: 'CPX-II with 4.85" travel.' },

  // ──── Sure-Loc — legacy/limited availability (Feradyne-owned)
  { brand: 'Sure-Loc', model: 'Supreme 550', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 20, max: 30 },
    notes: 'Legacy tournament slider. Limited availability — still on retailer shelves but production sporadic.' },
  { brand: 'Sure-Loc', model: 'Black Eagle', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 20, max: 30 },
    notes: 'Mid-tier Sure-Loc target slider. Legacy status.' },

  // ──── Spot-Hogg — target-tier sliders
  { brand: 'Spot Hogg', model: 'Tommy Hogg', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 28 },
    notes: 'Tournament-grade single-pin slider. MRT (Multiple Ring Tech) housing — 3/5/7 pin available. 2nd/3rd axis. Universal scope rod 1/4-28.' },
  { brand: 'Spot Hogg', model: 'Wrapped Hogg', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 28 },
    notes: 'Wrapped (full-circle pin guard) variant — popular for indoor/Vegas.' },

  // ──── CBE — target line
  { brand: 'CBE', model: 'Vertex Target', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 20, max: 30 },
    notes: '3" vertical travel, .002"/click elevation, detachable scope mount.' },
  { brand: 'CBE', model: 'TEK Target', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 20, max: 30 },
    notes: '3" vertical, rapid-drive elevation, Quad Track, micro W, optional 3rd axis.' },
  { brand: 'CBE', model: 'Engage Hybrid (target)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 26 },
    notes: 'Hybrid hunting/target single-pin slider w/ multi-pin housing option. Crossover use.' },

  // ──── HHA — target versions of Optimizer line
  { brand: 'HHA', model: 'Optimizer Tetra Tournament', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 26 },
    notes: 'Target/3D version of Tetra. Single-pin, DS-Tape system (sight in at 20 & 60 yd, generates cut tape).' },

  // ──── Ultraview — UV3 / UV3XL SE Target scopes (post-2023 emerging powerhouse)
  { brand: 'Ultraview', model: 'UV3 SE Target Scope', style: 'scope',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 32 },
    notes: 'Scope-only — mounts via 10-32 rod on Axcel/Shibuya/Sure-Loc/B3/CBE/Shrewd chassis. UV Cartridge swaps pin counts without losing sight marks. Built-in light, 6+ hr battery.' },
  { brand: 'Ultraview', model: 'UV3XL SE Target Scope (41mm)', style: 'scope',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 32 },
    notes: 'Larger 41mm scope housing for long-distance target — same UV Cartridge system as UV3.' },

  // ════════════════════════════════════════════════════════
  // PCA-COVERAGE EXPANSION (2026-06-15)
  // Names + taxonomy mirror the PCA sight library (docs/PCA-SIGHT-LIBRARY-
  // 2026-06-14.md) so we match their dropdown coverage (~120 sights). Pin
  // diameters + sight-radius ranges are CATEGORY ESTIMATES — the UI prompts the
  // user to measure their own tape slot with calipers. The tape style noted on
  // each (straight rail ≈ linear 1.0 · wheel/geared = magnified travel · angled
  // rail = straight×cosθ · click = discrete) drives per-sight travelScale
  // calibration in sightCalibration.ts as we measure each vs PCA.
  // ════════════════════════════════════════════════════════

  // ──── Apex Gear / Arc Systeme / Avalon / Axion
  { brand: 'Apex Gear', model: 'Covert', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 14, max: 24 },
    notes: 'Budget single-pin hunting slider. Straight-rail tape.' },
  { brand: 'Arc Systeme', model: 'SX200', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 26, max: 40 },
    notes: 'French target sight, carbon extension. Straight-rail tape.' },
  { brand: 'Avalon', model: 'Tec-X', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 38 },
    notes: 'Target sight (compound/recurve). Straight-rail tape.' },
  { brand: 'Axion', model: 'RD1', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 14, max: 24 },
    notes: 'Lightweight machined hunting slider. Straight-rail tape.' },

  // ──── Axcel — PCA models not yet covered
  { brand: 'Axcel', model: 'AccuHunter', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 14, max: 26 },
    notes: 'Hunting slider, T-slot dovetail. Straight-rail tape.' },
  { brand: 'Axcel', model: 'AccuTouch', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 28 },
    notes: 'AccuTouch carbon-bar slider. Straight-rail tape.' },
  { brand: 'Axcel', model: 'AX3000', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 26, max: 40 },
    notes: 'Target sight (compound/recurve). Straight-rail tape.' },
  { brand: 'Axcel', model: 'Achieve XP Compound', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 26, max: 42 },
    notes: 'Achieve XP target, compound config. Straight-rail tape.' },
  { brand: 'Axcel', model: 'Achieve XP Recurve', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 38 },
    notes: 'Achieve XP target, recurve config. Straight-rail tape.' },
  { brand: 'Axcel', model: 'Achieve XP 1.5 VR Compound', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 26, max: 42 },
    notes: 'Achieve XP 1.5 vertical-rail, compound. Straight-rail tape.' },
  { brand: 'Axcel', model: 'Achieve XP 1.5 VR Recurve', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 38 },
    notes: 'Achieve XP 1.5 vertical-rail, recurve. Straight-rail tape.' },
  { brand: 'Axcel', model: 'Achieve XP 2.0 VR Compound', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 26, max: 42 },
    notes: 'Achieve XP 2.0 vertical-rail, compound. Straight-rail tape.' },
  { brand: 'Axcel', model: 'Achieve XP 2.0 VR Recurve', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 38 },
    notes: 'Achieve XP 2.0 vertical-rail, recurve. Straight-rail tape.' },

  // ──── B3 Exact — many ship BOTH a straight and a wheel tape
  { brand: 'B3 Exact', model: '3D', style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 30 },
    notes: '3D/target slider. Straight-rail tape.' },
  { brand: 'B3 Exact', model: 'Competition Hunter', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 28 },
    notes: 'Hunting/competition slider. Straight-rail tape.' },
  { brand: 'B3 Exact', model: 'Linx (Straight Tape)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 28 },
    notes: 'Linx with the STRAIGHT (linear) tape — scale ≈ 1.0.' },
  { brand: 'B3 Exact', model: 'Linx (Wheel Tape)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 28 },
    notes: 'Linx with the WHEEL (geared) tape — magnified travel, needs per-sight scale.' },
  { brand: 'B3 Exact', model: 'Q2 3D', style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 30 },
    notes: 'Q2 3D slider. Straight-rail tape.' },
  { brand: 'B3 Exact', model: 'Q2 Target', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 40 },
    notes: 'Q2 target. Straight-rail tape.' },
  { brand: 'B3 Exact', model: 'Rize Hunter (Straight Tape)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'Rize Hunter with the STRAIGHT tape — scale ≈ 1.0.' },
  { brand: 'B3 Exact', model: 'Rize Hunter (Wheel Tape)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'Rize Hunter with the WHEEL tape — magnified travel, needs per-sight scale.' },
  { brand: 'B3 Exact', model: 'Rize Target', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 36 },
    notes: 'Rize target. Straight-rail tape.' },

  // ──── Black Gold — PCA models not yet covered
  { brand: 'Black Gold', model: 'Ascent Verdict Assault', style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 18, max: 28 },
    notes: 'Ascent Verdict Assault slider. Straight-rail tape.' },
  { brand: 'Black Gold', model: 'Competition 2A', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 40 },
    notes: 'Competition target, single-axis. Straight-rail tape.' },
  { brand: 'Black Gold', model: 'Competition 2ADT', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 40 },
    notes: 'Competition Dual-Trac target. Straight-rail tape (Dual Trac housing).' },
  { brand: 'Black Gold', model: 'Mountain Lite', style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 14, max: 22 },
    notes: 'Lightweight mountain-hunting slider. Straight-rail tape.' },
  { brand: 'Black Gold', model: 'Pro HD', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 },
    notes: 'Pro HD multi-pin hunter. Fixed pins (no tape).' },
  { brand: 'Black Gold', model: 'Pro Hunter HD', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 },
    notes: 'Pro Hunter HD multi-pin. Fixed pins (no tape).' },
  { brand: 'Black Gold', model: 'Recurve', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 36 },
    notes: 'Black Gold recurve target sight. Straight-rail tape.' },

  // ──── CBE — PCA models not yet covered
  { brand: 'CBE', model: '3D XL', style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 20, max: 34 },
    notes: '3D XL long-bar slider. Straight-rail tape.' },
  { brand: 'CBE', model: 'Axis', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'Axis hunting slider. Straight-rail tape.' },
  { brand: 'CBE', model: 'Elite 3D (30 click)', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 38 },
    notes: 'Elite 3D — 30-click-per-rev elevation. Discrete click marks.' },
  { brand: 'CBE', model: 'Tactic Hybrid', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'Tactic Hybrid pin+slider. Straight-rail tape.' },
  { brand: 'CBE', model: 'Tek Hybrid Pro', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'Tek Hybrid Pro pin+slider. Straight-rail tape.' },
  { brand: 'CBE', model: 'Trek', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'Trek hunting slider. Straight-rail tape.' },
  { brand: 'CBE', model: 'Trek 2V', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'Trek 2-pin vertical. Straight-rail tape.' },
  { brand: 'CBE', model: 'Trek Pro 2V', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'Trek Pro 2-pin vertical. Straight-rail tape.' },
  { brand: 'CBE', model: 'Trek Pro Micro 3V', style: 'slider',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 16, max: 26 },
    notes: 'Trek Pro Micro 3-pin vertical. Straight-rail tape.' },
  { brand: 'CBE', model: 'Vertex 3D Rapid Travel', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 38 },
    notes: 'Vertex 3D Rapid-Travel — geared fast elevation; verify tape scale vs straight.' },
  { brand: 'CBE', model: 'Vertex Elevate', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 38 },
    notes: 'Vertex Elevate target. Straight-rail tape.' },

  // ──── Copper John / Dark Owl / DS / GWS / Rocky Mountain / Sanlida / Viper
  { brand: 'Copper John', model: 'A.N.T.S.', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 },
    notes: 'A.N.T.S. multi-pin hunter. Fixed pins (no tape).' },
  { brand: 'Copper John', model: 'A.N.T.S. Evolution 2', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 14, max: 24 },
    notes: 'A.N.T.S. Evolution 2 adjustable. Straight-rail tape.' },
  { brand: 'Copper John', model: 'Dead Nuts 3 Mark IV', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 },
    notes: 'Dead Nuts 3 Mk IV multi-pin. Fixed pins (no tape).' },
  { brand: 'Dark Owl', model: 'Radium', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 28 },
    notes: 'Radium machined slider. Straight-rail tape.' },
  { brand: 'DS', model: 'Advantage', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 38 },
    notes: 'DS Advantage target. Straight-rail tape.' },
  { brand: 'GWS', model: 'AR-19', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 38 },
    notes: 'GWS AR-19 target. Straight-rail tape.' },
  { brand: 'Rocky Mountain', model: 'Driver', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 14, max: 24 },
    notes: 'Budget single-pin slider. Straight-rail tape.' },
  { brand: 'Sanlida', model: 'X10', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 36 },
    notes: 'Sanlida X10 target. Straight-rail tape.' },
  { brand: 'Viper', model: 'Sidewinder', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 },
    notes: 'Viper Sidewinder multi-pin. Fixed pins (no tape).' },

  // ──── Davis / Dead Ringer — click + wheel classes
  { brand: 'Davis', model: 'The System (10 Click)', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 38 },
    notes: 'The System — 10 clicks per revolution. Discrete click marks.' },
  { brand: 'Davis', model: 'The System (20 Click)', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 38 },
    notes: 'The System — 20 clicks per revolution. Discrete click marks.' },
  { brand: 'Dead Ringer', model: 'The Wheel', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'Geared WHEEL-tape slider — magnified travel, needs per-sight scale.' },

  // ──── Dialed Archery — Arxos (angled rail, like Pruf)
  { brand: 'Dialed Archery', model: 'Arxos', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 28 },
    notes: 'Arxos angled-rail slider (like Pruf) — tape ≈ straight×cosθ; calibrate vs PCA.' },

  // ──── Mathews — Bridge-Lock UV Slider (PCA lists under Mathews)
  { brand: 'Mathews', model: 'Bridge-Lock UV Slider', style: 'slider',
    pinDiameters: [0.010, 0.015, 0.019], sightRadius: { min: 18, max: 32 },
    notes: 'Mathews-branded Ultraview UV slider, Bridge-Lock mount. Same internals as UV Slider 2 — straight-rail tape.' },

  // ──── O'Neill's Classic Archery — SRU scope magnification variants
  { brand: "O'Neill's Classic Archery", model: 'SRU 1X', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 40 },
    notes: 'SRU target, 1X. Straight-rail tape.' },
  { brand: "O'Neill's Classic Archery", model: 'SRU 2X', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 40 },
    notes: 'SRU target, 2X. Straight-rail tape.' },
  { brand: "O'Neill's Classic Archery", model: 'SRU 4X', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 40 },
    notes: 'SRU target, 4X. Straight-rail tape.' },

  // ──── Option Archery
  { brand: 'Option', model: '4s/6s', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 28 },
    notes: 'Option 4s/6s slider. Straight-rail tape.' },
  { brand: 'Option', model: '8s', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 18, max: 30 },
    notes: 'Option 8s long-bar slider. Straight-rail tape.' },
  { brand: 'Option', model: 'Canyon Pounder', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 22 },
    notes: 'Canyon Pounder fixed/hybrid. Fixed pins (no tape) unless slider config.' },

  // ──── Redline — RL Torch ships BOTH straight + wheel tapes
  { brand: 'Redline', model: 'RL-1', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'RL-1 hunting slider. Straight-rail tape.' },
  { brand: 'Redline', model: 'RL-2', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'RL-2 hunting slider. Straight-rail tape.' },
  { brand: 'Redline', model: 'RL-2 DTM', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'RL-2 Dual-Trac-Mount slider. Straight-rail tape.' },
  { brand: 'Redline', model: 'RL Torch (Straight Tape)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'RL Torch with the STRAIGHT tape — scale ≈ 1.0.' },
  { brand: 'Redline', model: 'RL Torch (Wheel Tape)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'RL Torch with the WHEEL tape — magnified travel, needs per-sight scale.' },

  // ──── Shibuya — PCA models not yet covered
  { brand: 'Shibuya', model: 'Dual Click', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 38 },
    notes: 'Dual-Click target — discrete click marks.' },
  { brand: 'Shibuya', model: 'Ultima CP Pro', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 38 },
    notes: 'Ultima CP Pro compound target. Straight-rail tape.' },
  { brand: 'Shibuya', model: 'Ultima CP Pro G2', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 38 },
    notes: 'Ultima CP Pro G2 compound target. Straight-rail tape.' },
  { brand: 'Shibuya', model: 'Ultima CPX IV', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 38 },
    notes: 'Ultima CPX IV compound target. Straight-rail tape.' },
  { brand: 'Shibuya', model: 'Ultima RC II', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 36 },
    notes: 'Ultima RC II recurve target. Straight-rail tape.' },
  { brand: 'Shibuya', model: 'Ultima RC III', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 36 },
    notes: 'Ultima RC III recurve target. Straight-rail tape.' },
  { brand: 'Shibuya', model: 'Ultima RC Pro', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 36 },
    notes: 'Ultima RC Pro recurve target. Straight-rail tape.' },
  { brand: 'Shibuya', model: 'Ultima RC Pro G2', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 36 },
    notes: 'Ultima RC Pro G2 recurve target. Straight-rail tape.' },

  // ──── Sure-Loc — PCA models not yet covered
  { brand: 'Sure-Loc', model: 'Adrenaline', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 28 },
    notes: 'Adrenaline hunting slider. Straight-rail tape.' },
  { brand: 'Sure-Loc', model: 'Carbonic', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 40 },
    notes: 'Carbonic carbon-bar target. Straight-rail tape.' },
  { brand: 'Sure-Loc', model: 'Challenger', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 38 },
    notes: 'Challenger target. Straight-rail tape.' },
  { brand: 'Sure-Loc', model: 'Fury', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'Fury hunting slider. Straight-rail tape.' },
  { brand: 'Sure-Loc', model: 'One', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 24, max: 40 },
    notes: 'Sure-Loc One target. Straight-rail tape.' },
  { brand: 'Sure-Loc', model: 'QC', style: 'movable',
    pinDiameters: [0.010, 0.019], sightRadius: { min: 22, max: 38 },
    notes: 'QC quick-change target. Straight-rail tape.' },

  // ──── Sword — PCA models not yet covered
  { brand: 'Sword', model: 'Hawkeye', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 },
    notes: 'Hawkeye multi-pin hunter. Fixed pins (no tape).' },
  { brand: 'Sword', model: 'Judge', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 14, max: 24 },
    notes: 'Judge adjustable. Straight-rail tape.' },
  { brand: 'Sword', model: 'Jury', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 14, max: 24 },
    notes: 'Jury adjustable. Straight-rail tape.' },
  { brand: 'Sword', model: 'Punisher', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 },
    notes: 'Punisher multi-pin hunter. Fixed pins (no tape).' },
  { brand: 'Sword', model: 'Sniper', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 },
    notes: 'Sniper multi-pin hunter. Fixed pins (no tape).' },

  // ──── Trophy Ridge — SWFT ships BOTH straight + wheel tapes
  { brand: 'Trophy Ridge', model: 'Attack', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 },
    notes: 'Attack multi-pin hunter. Fixed pins (no tape).' },
  { brand: 'Trophy Ridge', model: 'Drive Slider', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'Drive single-pin slider. Straight-rail tape.' },
  { brand: 'Trophy Ridge', model: 'Optix', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 },
    notes: 'Optix multi-pin hunter. Fixed pins (no tape).' },
  { brand: 'Trophy Ridge', model: 'React', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 },
    notes: 'React (auto-set pins) multi-pin hunter. Fixed pins (no tape).' },
  { brand: 'Trophy Ridge', model: 'SWFT (Straight Tape)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'SWFT with the STRAIGHT tape — scale ≈ 1.0.' },
  { brand: 'Trophy Ridge', model: 'SWFT (Wheel Tape)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 16, max: 26 },
    notes: 'SWFT with the WHEEL tape — magnified travel, needs per-sight scale.' },
  { brand: 'Trophy Ridge', model: 'Trilogy', style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 20 },
    notes: 'Trilogy multi-pin hunter. Fixed pins (no tape).' },

  // ──── TruGlo — Range Rover line (+ Pendulum)
  { brand: 'TruGlo', model: 'Pendulum', style: 'fixed-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 12, max: 18 },
    notes: 'Pendulum treestand sight — swinging pin auto-compensates for steep angles. No tape.' },
  { brand: 'TruGlo', model: 'Range Rover Standard', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 14, max: 24 },
    notes: 'Range Rover single-pin slider. Straight-rail tape.' },
  { brand: 'TruGlo', model: 'Range Rover Pro', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 14, max: 26 },
    notes: 'Range Rover Pro slider. Straight-rail tape.' },
  { brand: 'TruGlo', model: 'Range Rover M4', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 14, max: 26 },
    notes: 'Range Rover M4 slider. Straight-rail tape.' },
  { brand: 'TruGlo', model: 'Range Rover (Wheel)', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 14, max: 26 },
    notes: 'Range Rover WHEEL-tape variant — magnified travel, needs per-sight scale.' },

  // ──── Ultraview — UV Slider (gen 1)
  { brand: 'Ultraview', model: 'UV Slider', style: 'slider',
    pinDiameters: [0.010, 0.015, 0.019], sightRadius: { min: 18, max: 32 },
    notes: 'First-gen UV Slider (pre-UVS2). Straight-rail tape, scale ≈ 1.0.' },

  // ────────────────────────────────────────────────────────
  // GENERIC OPTIONS — for users with unlisted sights
  // ────────────────────────────────────────────────────────
  { brand: 'Generic', model: 'Single-Pin Slider', style: 'slider',
    pinDiameters: [0.010, 0.019, 0.029], sightRadius: { min: 14, max: 32 } },
  { brand: 'Generic', model: '3-Pin Multi',       style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 22 } },
  { brand: 'Generic', model: '5-Pin Multi',       style: 'multi-pin',
    pinDiameters: [0.019, 0.029], sightRadius: { min: 14, max: 22 } },
  { brand: 'Generic', model: 'Fixed-Pin Hunter',  style: 'fixed-pin',
    pinDiameters: [0.019], sightRadius: { min: 14, max: 20 } },
] as const;
